'use client';
import { useState } from 'react';
import Navbar from '@/components/Navbar';
import PageHero from '@/components/PageHero';
import Footer from '@/components/Footer';
import { Mail, Phone, MapPin, Clock, Send, ChevronRight, CheckCircle, MessageSquare, Headphones, Building } from 'lucide-react';

export const metadata = {
  title: 'Contact FlightAirHub to get assistance with flight booking.',
  description: "For any booking-related concerns or issues, contact FlightAirHub. We are happy to help you over the phone, email, or chat. Ring or ping anytime you need.",
  alternates: { canonical: '/contact-us' },
  robots: { index: true, follow: true },
};

const CONTACT_METHODS = [
  {
    icon: Headphones,
    title: 'Get 24/7 Assistance',
    desc: 'Dial +1-800-986-4713 to get assistance from our experts any time you need.',
    action: '+1-800-986-4713',
    actionLabel: 'Call now',
    href: 'tel:+18009864713',
    color: 'bg-sky-50 text-sky-500',
  },
  {
    icon: Mail,
    title: 'Send in your Query',
    desc: 'Drop us an email to get in touch with our representative regarding your issues.',
    action: 'support@flightairhub.com',
    actionLabel: 'Send email',
    href: 'mailto:support@flightairhub.com',
    color: 'bg-emerald-50 text-emerald-500',
  },
  {
    icon: MessageSquare,
    title: 'Discuss your Issues with our Representative Live',
    desc: 'Chat with us and receive quick assistance to resolve your bookings-related issues.',
    action: 'Start a chat',
    actionLabel: 'Chat now',
    href: '/contact-us',
    color: 'bg-violet-50 text-violet-500',
  },
];

const OFFICES = [
  { city: 'Cityville (HQ)', address: '1234 Horizon Lane, Suite 500, Cityville, 32402, USA', phone: '+1-800-986-4713' },
];

export default function ContactClient() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: 'General Inquiry', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  function handleChange(e) {
    setForm(v => ({ ...v, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    await new Promise(r => setTimeout(r, 1200)); // simulate send
    setSubmitted(true);
    setLoading(false);
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className='hero-contact'>
        <PageHero
          image="/images/contact-hero.jpg"
          imageAlt="Contact FlightAirHub"
          breadcrumb={[{ label: 'Contact Us' }]}
          align="left"
          title="Any Queries or Concerns? Ring or Ping Anytime"
          subtitle="FlightAirHub is committed to making your bookings hassle-free"
        />
      </div>

      {/* Contact methods */}
      <section className="py-16 px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {CONTACT_METHODS.map(({ icon: Icon, title, desc, action, actionLabel, href, color }) => (
              <div key={title} className="bg-white rounded-2xl p-7 border border-slate-100 hover:shadow-lg transition-shadow">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 ${color}`}>
                  <Icon size={24} />
                </div>
                <h3 className="font-semibold text-slate-900 text-lg mb-2">{title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed mb-4">{desc}</p>
                <a href={href} className="text-sky-500 font-semibold text-sm hover:text-sky-700 transition-colors flex items-center gap-1">
                  {action} <ChevronRight size={14} />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact form + offices */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-start">

          {/* Form */}
          <div className='p-6 bg-slate-100 rounded-[10px]'>
            <p className="text-sky-500 font-semibold text-sm tracking-widest uppercase mb-3">Email Us</p>
            <h2 className="font-display text-4xl font-bold text-slate-900 mb-8">Send a Message</h2>

            {submitted ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-10 text-center">
                <CheckCircle size={52} className="text-emerald-500 mx-auto mb-4" />
                <h3 className="font-display font-bold text-2xl text-slate-900 mb-2">Message received!</h3>
                <p className="text-slate-500">We'll get back to you at <strong>{form.email}</strong> within 4 business hours.</p>
                <button onClick={() => { setSubmitted(false); setForm({ name: '', email: '', phone: '', subject: 'General Inquiry', message: '' }); }} className="mt-6 text-sky-500 font-semibold text-sm hover:text-sky-700 transition-colors">
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1.5">Full Name *</label>
                    <input
                      name="name"
                      required
                      value={form.name}
                      onChange={handleChange}
                      className="w-full border border-slate-200 bg-white text-slate-800 px-4 py-3 rounded-xl text-sm focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition-all"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1.5">Phone</label>
                    <input
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      className="w-full border bg-white border-slate-200 text-slate-800 px-4 py-3 rounded-xl text-sm focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition-all"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1.5">Email Address *</label>
                  <input
                    name="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={handleChange}
                    className="w-full border bg-white border-slate-200 text-slate-800 px-4 py-3 rounded-xl text-sm focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition-all"
                    placeholder="you@example.com"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1.5">Subject</label>
                  <select
                    name="subject"
                    value={form.subject}
                    onChange={handleChange}
                    className="w-full border border-slate-200 text-slate-800 px-4 py-3 rounded-xl text-sm focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition-all bg-white"
                  >
                    <option>General Inquiry</option>
                    <option>Booking Issue</option>
                    <option>Refund Request</option>
                    <option>Technical Issue</option>
                    <option>Partnership</option>
                    <option>Press / Media</option>
                    <option>Other</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1.5">Message *</label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    value={form.message}
                    onChange={handleChange}
                    className="w-full bg-white border border-slate-200 text-slate-800 px-4 py-3 rounded-xl text-sm focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition-all resize-none"
                    placeholder="Tell us how we can help..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-sky-500 hover:bg-sky-600 disabled:bg-sky-300 text-white font-semibold py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Sending…</>
                  ) : (
                    <><Send size={17} /> Send Message</>
                  )}
                </button>
              </form>
            )}
          </div>

          {/* Offices */}
          <div>
            <p className="text-sky-500 font-semibold text-sm tracking-widest uppercase mb-3">Find Us</p>
            <h2 className="font-display text-4xl font-bold text-slate-900 mb-8">Our Offices</h2>

            <div className="space-y-5 mb-10">
              {OFFICES.map(({ city, address, phone }) => (
                <div key={city} className="flex gap-4 p-5 bg-slate-50 rounded-2xl border border-slate-100 hover:border-sky-200 transition-colors">
                  <div className="w-10 h-10 bg-sky-500 rounded-xl flex items-center justify-center shrink-0 mt-0.5">
                    <Building size={18} className="text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">{city}</h4>
                    <p className="text-slate-500 text-sm leading-relaxed flex items-start gap-1.5">
                      <MapPin size={13} className="mt-0.5 shrink-0 text-slate-400" /> {address}
                    </p>
                    <p className="text-slate-500 text-sm mt-1 flex items-center gap-1.5">
                      <Phone size={13} className="shrink-0 text-slate-400" /> {phone}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Working hours */}
            <div className="bg-sky-500 rounded-2xl p-6 text-white">
              <div className="flex items-center gap-3 mb-4">
                <Clock size={20} className="opacity-80" />
                <h4 className="font-semibold text-lg">Business Hours</h4>
              </div>
              <div className="space-y-2 text-sm">
                {[['Phone & Chat', '7 AM – 11 PM, Monday to Sunday'], ['Email Support', '7 AM – 11 PM, Monday to Sunday'], ['Office Visits', '7 AM – 11 PM, Monday to Sunday']].map(([label, hours]) => (
                  <div key={label} className="flex justify-between items-center py-1.5 border-b border-white/20 last:border-0">
                    <span className="text-sky-100">{label}</span>
                    <span className="font-medium">{hours}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
